import React from 'react';
import { useAuth } from '../context/AuthContext';
import { hasFeature, getRequiredTier } from '../config/features';
import UpgradePrompt from './UpgradePrompt';

/**
 * FeatureGate - Conditionally render content based on user's subscription tier
 *
 * @param {string} feature - The feature key to check (from features.js)
 * @param {React.ReactNode} children - Content to render if user has access
 * @param {React.ReactNode} fallback - Optional custom fallback if user doesn't have access
 * @param {string} promptVariant - 'card' (default), 'inline', 'banner', or 'hidden'
 *
 * Usage:
 *   <FeatureGate feature="expense_tracking">
 *     <ExpenseTracker />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="square_integration" promptVariant="banner">
 *     <SquareSettings />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="advanced_reports" fallback={<BasicReports />}>
 *     <AdvancedReports />
 *   </FeatureGate>
 */
function FeatureGate({ feature, children, fallback, promptVariant = 'card' }) {
  const { user } = useAuth();

  // Get user's subscription tier (default to 'free' if not set)
  const userTier = user?.subscriptionTier?.toLowerCase() || 'free';

  // Check if user has access to this feature
  const canAccess = hasFeature(userTier, feature);

  if (canAccess) {
    return <>{children}</>;
  }

  // User doesn't have access - show fallback or upgrade prompt
  if (fallback) {
    return <>{fallback}</>;
  }

  // If promptVariant is 'hidden', render nothing
  if (promptVariant === 'hidden') {
    return null;
  }

  // Show upgrade prompt
  const requiredTier = getRequiredTier(feature);
  return <UpgradePrompt feature={feature} requiredTier={requiredTier} variant={promptVariant} />;
}

/**
 * useFeatureAccess - Hook to check feature access in components
 *
 * Usage:
 *   const { canAccess, requiredTier } = useFeatureAccess('expense_tracking');
 *   if (canAccess) { ... }
 */
export function useFeatureAccess(feature) {
  const { user } = useAuth();
  const userTier = user?.subscriptionTier?.toLowerCase() || 'free';
  const canAccess = hasFeature(userTier, feature);
  const requiredTier = getRequiredTier(feature);

  return { canAccess, requiredTier, userTier };
}

export default FeatureGate;
